import React, { useEffect, useState } from "react";
import { useLive } from "@/lib/live";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Trash, ArrowCounterClockwise, User, Warning, Package, Eye, X,
  CurrencyInr, Phone, MapPin, Calendar, Clock, Info, Receipt,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function RecycleBinPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [preview, setPreview] = useState(null); // trashed order to view

  useLive(() => { load(); });
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/trash/orders");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const restore = async (id, num) => {
    setBusyId(id);
    try {
      await api.post(`/trash/orders/${id}/restore`);
      toast.success(`Order #${num} restored`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusyId(null); }
  };

  const purge = async (id, num) => {
    if (!window.confirm(`Permanently delete order #${num}? This cannot be undone.`)) return;
    setBusyId(id);
    try {
      await api.delete(`/trash/orders/${id}`);
      toast.success(`Order #${num} permanently deleted`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusyId(null); }
  };

  const emptyTrash = async () => {
    if (!window.confirm("Permanently delete ALL trashed orders? This cannot be undone.")) return;
    try {
      const { data } = await api.post("/trash/empty");
      toast.success(`${data.deleted} orders permanently deleted`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="recycle-bin-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-mono-num tracking-widest text-slate-400">// TRASH</div>
          <h1 className="font-heading text-3xl font-black text-white">Recycle Bin</h1>
          <p className="text-slate-400 text-sm mt-1">
            Deleted orders live here. <span className="text-orange-300">Order number pe click karo</span> — bina restore kiye poora data preview kar sakte ho. Only primary admin can restore or permanently delete.
          </p>
        </div>
        {rows.length > 0 && (
          <Button
            onClick={emptyTrash}
            variant="outline"
            className="rounded-sm border-red-500/50 text-red-400 hover:bg-red-500/10 h-11 font-heading tracking-widest"
            data-testid="empty-trash-btn"
          >
            <Trash size={16} className="mr-2" weight="bold" /> EMPTY TRASH
          </Button>
        )}
      </div>

      <div className="surface rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-slate-800 text-[10px] font-mono-num tracking-widest text-slate-500">
          <div className="col-span-2">ORDER</div>
          <div className="col-span-2">CUSTOMER</div>
          <div className="col-span-2 text-right">AMOUNT</div>
          <div className="col-span-3">DELETED BY</div>
          <div className="col-span-1">DATE</div>
          <div className="col-span-2 text-right">ACTIONS</div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <Trash size={40} className="mx-auto mb-3 text-slate-700" />
            Recycle bin is empty. Deleted orders will appear here.
          </div>
        ) : rows.map((o) => (
          <div key={o.id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-slate-800 hover:bg-slate-800/40 items-center" data-testid={`trash-row-${o.id}`}>
            <div className="col-span-2">
              <button
                type="button"
                onClick={() => setPreview(o)}
                className="font-mono-num text-orange-400 font-bold hover:text-orange-300 hover:underline underline-offset-4 decoration-orange-500/50 flex items-center gap-1.5 group"
                data-testid={`view-trash-${o.id}`}
                title="Click to preview deleted order data"
              >
                #{o.order_number || "—"}
                <Eye size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" weight="bold" />
              </button>
            </div>
            <div className="col-span-2 text-slate-200 truncate">
              {o.customer_name || "—"}
              {o.mobile && (
                <div className="text-[10px] font-mono-num text-slate-500">{o.mobile}</div>
              )}
            </div>
            <div className="col-span-2 text-right font-mono-num text-cyan-300">
              ₹{(o.total_amount || 0).toLocaleString("en-IN")}
            </div>
            <div className="col-span-3 text-xs">
              <div className="flex items-center gap-1 text-slate-300">
                <User size={12} className="text-red-400" />
                {o.deleted_by_name || o.deleted_by || "unknown"}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {o.deleted_by_role || "—"} · {o.deleted_by || ""}
              </div>
            </div>
            <div className="col-span-1 text-[10px] font-mono-num text-slate-500">
              {o.deleted_at ? new Date(o.deleted_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"}
            </div>
            <div className="col-span-2 flex items-center gap-1 justify-end">
              <Button
                onClick={() => restore(o.id, o.order_number)}
                disabled={busyId === o.id}
                size="sm"
                className="h-8 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white font-mono-num text-[10px] tracking-widest"
                data-testid={`restore-${o.id}`}
              >
                <ArrowCounterClockwise size={12} className="mr-1" /> RESTORE
              </Button>
              <Button
                onClick={() => purge(o.id, o.order_number)}
                disabled={busyId === o.id}
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0 border-red-500/40 text-red-400 hover:bg-red-500/10"
                data-testid={`purge-${o.id}`}
                title="Delete forever"
              >
                <Trash size={14} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {rows.length > 0 && (
        <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-2">
          <Package size={12} /> {rows.length} order{rows.length === 1 ? "" : "s"} in trash. Restore anytime, or empty the bin to reclaim space.
        </div>
      )}

      <TrashPreviewDialog
        order={preview}
        onClose={() => setPreview(null)}
        onRestore={() => { const p = preview; setPreview(null); restore(p.id, p.order_number); }}
        onPurge={() => { const p = preview; setPreview(null); purge(p.id, p.order_number); }}
        busy={busyId === preview?.id}
      />
    </div>
  );
}

function TrashPreviewDialog({ order, onClose, onRestore, onPurge, busy }) {
  if (!order) return null;
  const items = order.items || [];
  const payments = order.payments || [];
  const paid = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const bal = Number(order.total_amount || 0) - paid;
  const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;
  const dt = (s) =>
    s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-y-auto bg-slate-900 border-slate-700 text-white rounded-sm"
        data-testid="trash-preview-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 pr-8">
            <Trash size={18} className="text-red-400" weight="fill" />
            <span className="text-red-300">DELETED · READ-ONLY PREVIEW</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Deletion banner */}
          <div className="rounded-sm border border-red-500/40 bg-red-500/10 p-3 flex items-start gap-2">
            <Warning size={18} className="text-red-400 shrink-0 mt-0.5" weight="fill" />
            <div className="text-xs text-red-200 leading-relaxed">
              Ye order <b>{dt(order.deleted_at)}</b> ko <b className="text-red-300">{order.deleted_by_name || order.deleted_by || "unknown"}</b> ({order.deleted_by_role || "—"}) ne delete kiya tha.
              Data restore nahi hua hai — sirf preview hai.
            </div>
          </div>

          {/* Header block */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="surface p-3 rounded-sm md:col-span-1">
              <div className="text-[10px] font-mono-num tracking-widest text-slate-400">// ORDER</div>
              <div className="font-mono-num text-orange-400 font-black text-2xl mt-1">#{order.order_number}</div>
              <div className="text-[11px] text-slate-500 mt-1">{order.status || "—"}</div>
              <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                <Calendar size={11} /> Created: {dt(order.created_at)}
              </div>
              {order.delivered_at && (
                <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                  <Clock size={11} /> Delivered: {dt(order.delivered_at)}
                </div>
              )}
            </div>
            <div className="surface p-3 rounded-sm md:col-span-2">
              <div className="text-[10px] font-mono-num tracking-widest text-slate-400">// CUSTOMER</div>
              <div className="text-slate-100 font-semibold text-lg mt-1">{order.customer_name || "—"}</div>
              {order.mobile && (
                <div className="text-cyan-300 font-mono-num text-sm mt-1 flex items-center gap-1">
                  <Phone size={12} /> {order.mobile}
                </div>
              )}
              {order.address && (
                <div className="text-[11px] text-slate-400 mt-1 flex items-start gap-1">
                  <MapPin size={11} className="mt-0.5 shrink-0" /> {order.address}
                </div>
              )}
              {order.customer_type && (
                <div className="text-[10px] font-mono-num text-slate-500 mt-1 tracking-widest">
                  TYPE: {order.customer_type}
                </div>
              )}
            </div>
          </div>

          {/* Money summary */}
          <div className="surface p-3 rounded-sm">
            <div className="text-[10px] font-mono-num tracking-widest text-slate-400 mb-2 flex items-center gap-1">
              <CurrencyInr size={11} /> FINANCIALS
            </div>
            <div className="grid grid-cols-3 gap-3">
              <MetricCell label="Total" value={fmt(order.total_amount)} tone="cyan" />
              <MetricCell label="Paid" value={fmt(paid)} tone="emerald" />
              <MetricCell label="Balance" value={fmt(bal)} tone={bal > 0 ? "orange" : "emerald"} />
            </div>
          </div>

          {/* Items */}
          {items.length > 0 && (
            <div className="surface rounded-sm overflow-hidden">
              <div className="text-[10px] font-mono-num tracking-widest text-slate-400 px-3 py-2 border-b border-slate-800">
                ITEMS ({items.length})
              </div>
              <div className="divide-y divide-slate-800">
                {items.map((it, i) => (
                  <div key={i} className="px-3 py-2 grid grid-cols-12 gap-2 text-sm items-center">
                    <div className="col-span-5 text-slate-200">
                      <div className="text-[10px] font-mono-num text-slate-500">#{i + 1}</div>
                      <div className="truncate">
                        {it.item_name || it.description || it.thickness || "Item"}
                      </div>
                    </div>
                    <div className="col-span-3 text-slate-400 text-xs font-mono-num">
                      {it.width_inches ? `${it.width_inches}" × ${it.height_inches}"` : ""}
                      {it.qty ? ` × ${it.qty}` : ""}
                    </div>
                    <div className="col-span-2 text-right text-slate-400 text-xs font-mono-num">
                      {it.sqft ? `${it.sqft.toFixed(2)} sqft` : "—"}
                    </div>
                    <div className="col-span-2 text-right font-mono-num text-cyan-300">
                      ₹{Number(it.amount || 0).toLocaleString("en-IN")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payments */}
          {payments.length > 0 && (
            <div className="surface rounded-sm overflow-hidden">
              <div className="text-[10px] font-mono-num tracking-widest text-slate-400 px-3 py-2 border-b border-slate-800 flex items-center gap-1">
                <Receipt size={11} /> PAYMENTS ({payments.length})
              </div>
              <div className="divide-y divide-slate-800">
                {payments.map((p, i) => (
                  <div key={i} className="px-3 py-2 grid grid-cols-12 gap-2 text-xs items-center">
                    <div className="col-span-3 font-mono-num text-slate-400">
                      {p.created_at ? dt(p.created_at) : "—"}
                    </div>
                    <div className="col-span-3 text-slate-300">{p.mode || "—"}</div>
                    <div className="col-span-3 text-slate-400 truncate">{p.note || "—"}</div>
                    <div className="col-span-3 text-right font-mono-num text-emerald-400">
                      ₹{Number(p.amount || 0).toLocaleString("en-IN")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {(order.notes || order.customer_notes) && (
            <div className="surface p-3 rounded-sm">
              <div className="text-[10px] font-mono-num tracking-widest text-slate-400 mb-1 flex items-center gap-1">
                <Info size={11} /> NOTES
              </div>
              <div className="text-slate-300 text-sm whitespace-pre-wrap">
                {order.notes || order.customer_notes}
              </div>
            </div>
          )}

          {/* Action bar */}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-800">
            <div className="text-[10px] text-slate-500 font-mono-num tracking-widest">
              READ-ONLY · NO EDITS SAVED
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={onClose}
                className="rounded-sm border-slate-600 text-slate-300 hover:bg-slate-800 h-9"
                data-testid="trash-preview-close"
              >
                Close
              </Button>
              <Button
                onClick={onPurge}
                disabled={busy}
                variant="outline"
                className="rounded-sm border-red-500/50 text-red-400 hover:bg-red-500/10 h-9 font-heading tracking-widest"
                data-testid="trash-preview-purge"
              >
                <Trash size={14} className="mr-1.5" /> DELETE FOREVER
              </Button>
              <Button
                onClick={onRestore}
                disabled={busy}
                className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white h-9 font-heading tracking-widest"
                data-testid="trash-preview-restore"
              >
                <ArrowCounterClockwise size={14} className="mr-1.5" /> RESTORE
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MetricCell({ label, value, tone }) {
  const toneCls = {
    cyan: "text-cyan-300",
    emerald: "text-emerald-400",
    orange: "text-orange-400",
  }[tone] || "text-slate-100";
  return (
    <div>
      <div className="text-[10px] font-mono-num tracking-widest text-slate-500">{label.toUpperCase()}</div>
      <div className={"font-mono-num font-black text-lg " + toneCls}>{value}</div>
    </div>
  );
}
